'use client';

import { AnimatePresence, motion, MotionConfig } from 'motion/react';
import { FaRegLightbulb, FaSearch, FaUser } from 'react-icons/fa';
import { twJoin } from 'tailwind-merge';

import { Link } from '@/components/Link';
import { ResizablePanel } from '@/components/ResizablePanel';
import { useLayout } from '@/context/layout-context';
import { clsx } from '@/utils/helpers';
import { ZmyleLinks } from '@/utils/paths';

import {
  type NavigationButton,
  useMobileNavigation,
} from '../mobile-navigation-context';
import { Search } from '../Search';
import { SolutionsMenu } from '../SolutionsMenu';

import { MenuItemButton } from './MenuItemButton';

export function Menu() {
  const { isOpen, onOpenNavigation, onCloseNavigation, activeNav } =
    useMobileNavigation();

  const { content } = useLayout();

  const { search, solutions, login } = content.mobileNav;

  function onMenuButtonClick(name: NavigationButton) {
    return () => {
      if (activeNav === name) return onCloseNavigation();

      onOpenNavigation(name);
    };
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed top-0 left-0 z-[14] h-screen w-screen bg-blue-800/60"
          onClick={onCloseNavigation}
        />
      )}

      <div
        className={twJoin(
          'fixed bottom-0 z-15 flex w-full flex-col',
          'rounded-t-xl border-t border-gray-100 bg-white',
        )}
      >
        <div className="flex w-full items-center justify-center gap-10 pt-2.5 pb-2">
          <MenuItemButton
            isActive={activeNav === 'search'}
            icon={FaSearch}
            label={search}
            onClick={onMenuButtonClick('search')}
          />

          <MenuItemButton
            isActive={activeNav === 'solutions'}
            icon={FaRegLightbulb}
            label={solutions}
            onClick={onMenuButtonClick('solutions')}
          />

          <Link
            className="hover:no-underline"
            href={`${window.location.origin}/${ZmyleLinks.LOGIN}`}
            target="_blank"
            isExternal
          >
            <MenuItemButton
              isActive={activeNav === 'login'}
              icon={FaUser}
              label={login}
            />
          </Link>
        </div>

        <MotionConfig transition={{ duration: 0.2 }}>
          <motion.div
            className={clsx(
              'w-full bg-gray-50 shadow-inner',
              isOpen ? 'p-5' : 'p-0',
            )}
          >
            <AnimatePresence mode="wait" initial={false}>
              {isOpen && (
                <motion.section
                  className="flex w-full"
                  initial="collapsed"
                  animate="open"
                  exit="collapsed"
                  variants={{
                    open: { opacity: 1, height: 'auto' },
                    collapsed: { opacity: 0, height: 0 },
                  }}
                >
                  <ResizablePanel
                    className="flex flex-1 items-center justify-center"
                    containerProps={{ transition: { duration: 0.2 } }}
                  >
                    {activeNav === 'search' && <Search />}

                    {activeNav === 'solutions' && <SolutionsMenu />}
                  </ResizablePanel>
                </motion.section>
              )}
            </AnimatePresence>
          </motion.div>
        </MotionConfig>
      </div>
    </>
  );
}
